import { useCallback, useId, useMemo, useRef, useState, ChangeEvent } from "react";
import { CountryCode } from "libphonenumber-js";
import { COUNTRIES, getCountryByCode } from "../data/countries";
import { formatNationalNumber } from "../utils/phone";
import { useClickOutside } from "../hooks/useClickOutside";
import dropdownChevron from "../assets/dropdown-chevron.svg";

interface PhoneFieldProps {
  countryCode: CountryCode;
  nationalNumber: string;
  onCountryChange: (code: CountryCode) => void;
  onNumberChange: (nationalNumber: string) => void;
  error?: boolean;
  helperText?: string;
}

const PhoneField = ({
  countryCode,
  nationalNumber,
  onCountryChange,
  onNumberChange,
  error = false,
  helperText,
}: PhoneFieldProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const searchId = useId();
  const country = getCountryByCode(countryCode);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setQuery("");
  }, []);

  useClickOutside(containerRef, closeMenu, menuOpen);

  const filteredCountries = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.dial.includes(q) ||
        c.code.toLowerCase().includes(q)
    );
  }, [query]);

  const handleNumberInput = (event: ChangeEvent<HTMLInputElement>) => {
    onNumberChange(formatNationalNumber(event.target.value, countryCode));
  };

  const handleCountrySelect = (code: CountryCode) => {
    onCountryChange(code);
    if (nationalNumber) {
      onNumberChange(formatNationalNumber(nationalNumber, code));
    }
    closeMenu();
  };

  return (
    <div className="flex flex-col gap-1 w-full min-w-0 max-w-full">
      <label htmlFor="phone-input" className="form-field-label">
        Phone
      </label>
      <div
        ref={containerRef}
        className={`form-input-frame relative${error ? " is-error" : ""}`}
      >
        <div className="relative shrink min-w-0 max-w-[42%]">
          <button
            type="button"
            className="flex items-center gap-1 max-w-full bg-transparent border-0 p-0 cursor-pointer text-white text-base leading-6 font-normal"
            aria-haspopup="listbox"
            aria-expanded={menuOpen}
            aria-controls={listboxId}
            aria-label={`Country: ${country.name}`}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="truncate">{country.name}</span>
            <span className="inline-flex w-5 h-5 items-center justify-center shrink-0" aria-hidden="true">
              <img
                src={dropdownChevron}
                alt=""
                width={10}
                height={6}
                className="w-2.5 h-1.5"
              />
            </span>
          </button>

          {menuOpen ? (
            <div
              id={listboxId}
              role="listbox"
              aria-label="Select country"
              className="dropdown-panel absolute top-[calc(100%+6px)] left-0 z-30 w-[min(260px,calc(100vw-4rem))] max-h-[260px] overflow-hidden flex flex-col"
            >
              <label htmlFor={searchId} className="sr-only">
                Search country
              </label>
              <input
                id={searchId}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country"
                className="dropdown-search"
                autoFocus
              />
              <div className="overflow-y-auto max-h-[200px]">
                {filteredCountries.map((option) => {
                  const selected = option.code === countryCode;
                  return (
                    <button
                      key={option.code}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`dropdown-option${selected ? " is-selected" : ""}`}
                      onClick={() => handleCountrySelect(option.code)}
                    >
                      <span>{option.name}</span>
                      <span className="dropdown-option-meta">{option.dial}</span>
                    </button>
                  );
                })}
                {filteredCountries.length === 0 ? (
                  <div className="px-2.5 py-2 text-sm text-neutral-50">
                    No countries found
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        <span className="shrink-0 text-white text-base leading-6" aria-hidden="true">
          {country.dial}
        </span>

        <input
          id="phone-input"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={nationalNumber}
          onChange={handleNumberInput}
          placeholder="e.g. (800) 555-0299"
          className="form-field-input"
        />
      </div>
      {error && helperText ? (
        <p className="form-field-error" role="alert">
          {helperText}
        </p>
      ) : null}
    </div>
  );
};

export default PhoneField;
